
import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    nom: '',
    telephone: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nom || !formData.telephone || !formData.message) {
      return;
    }

    if (formData.message.length > 500) {
      alert('Le message ne peut pas dépasser 500 caractères.');
      return;
    }

    // Créer le lien mailto avec les informations du formulaire
    const subject = encodeURIComponent(`Demande de dépannage électrique - ${formData.nom}`);
    const body = encodeURIComponent(
      `Bonjour,\n\n` +
      `Nom: ${formData.nom}\n` +
      `Téléphone: ${formData.telephone}\n\n` +
      `Message:\n${formData.message}\n\n` +
      `Cordialement,\n${formData.nom}`
    );
    
    const mailtoLink = `mailto:contact@depannage-urgent-electricite.fr?subject=${subject}&body=${body}`;
    
    // Ouvrir le client de messagerie
    window.location.href = mailtoLink;
    
    // Réinitialiser le formulaire après envoi
    setFormData({ nom: '', telephone: '', message: '' });
  };

  return (
    <section id="contact" className="py-20 bg-stone-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-nunito">
            Contact
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-nunito">
            Une urgence électrique ? Contactez-nous immédiatement
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h3 className="text-3xl font-bold text-gray-800 mb-8 font-nunito">
                Besoin d'un électricien ?
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-phone-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 font-nunito">Téléphone</h4>
                    <a href="tel:+33656847324" className="text-red-600 font-semibold text-lg cursor-pointer font-nunito">
                      06 56 84 73 24
                    </a>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-mail-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 font-nunito">Email</h4>
                    <p className="text-gray-600 font-nunito">contact@depannage-urgent-electricite.fr</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-map-pin-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 font-nunito">Zone d'intervention</h4>
                    <p className="text-gray-600 font-nunito">Nantes et alentours</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-time-line text-xl text-red-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 font-nunito">Disponibilité</h4>
                    <p className="text-gray-600 font-nunito">7j/7 pour les urgences</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-6 bg-red-50 rounded-2xl">
                <h4 className="font-bold text-red-800 mb-2 font-nunito">Urgence électrique ?</h4>
                <p className="text-red-700 mb-4 font-nunito">
                  N'attendez pas, appelez-nous directement pour une intervention rapide.
                </p>
                <a
                  href="tel:+33656847324"
                  className="inline-flex items-center bg-red-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer shadow-lg font-nunito"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Appeler maintenant
                </a>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 font-nunito">
                Nous contacter
              </h3>
              
              <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="nom" className="block text-sm font-semibold text-gray-707 mb-2 font-nunito">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    required
                    value={formData.nom}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm font-nunito"
                    placeholder="Votre nom"
                  />
                </div>
                
                <div>
                  <label htmlFor="telephone" className="block text-sm font-semibold text-gray-707 mb-2 font-nunito">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    required
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm font-nunito"
                    placeholder="Votre numéro de téléphone"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-707 mb-2 font-nunito">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none font-nunito"
                    placeholder="Décrivez votre problème électrique..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1 font-nunito">
                    {formData.message.length}/500 caractères
                  </p>
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-red-600 text-white py-3 px-6 rounded-full font-semibold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer shadow-lg font-nunito"
                >
                  <i className="ri-mail-send-line mr-2"></i>
                  Ouvrir dans ma messagerie
                </button>
                
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                  <div className="flex items-start">
                    <i className="ri-information-line text-blue-600 mr-2 mt-0.5"></i>
                    <p className="text-blue-800 text-sm font-nunito">
                      Le formulaire ouvrira votre logiciel de messagerie avec les informations pré-remplies. 
                      Votre message sera envoyé directement à notre équipe.
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
